/**
 * Responsibility: read one video frame and say how sharp and how new it is.
 *
 * Both answers come off the same downscaled grey copy, because drawing the
 * frame to a canvas and reading the pixels back is the expensive part and
 * doing it twice on a phone at three frames a second is the difference
 * between a preview and a slideshow.
 *
 * **Sharpness** is Laplacian variance - the standard cheap focus measure.
 * **Newness** is the mean absolute difference from the previous frame,
 * which is what tells a scanner that the card in front of it has been
 * swapped for another one rather than simply held still.
 */

/** The width the frame is sampled at. Small enough to be free, big enough
 *  that a blurred card and a sharp one do not measure the same. */
const SAMPLE_WIDTH = 220;

export type FrameStats = {
  /** Laplacian variance. Higher is sharper. */
  sharpness: number;
  /** Mean absolute grey difference from the frame before, 0-255. */
  change: number;
};

/** Grey values of the last frame, so change is measured against something. */
export type FrameMemory = { grey: Float32Array | null };

export function newMemory(): FrameMemory {
  return { grey: null };
}

export function readFrame(
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement,
  memory: FrameMemory,
): FrameStats {
  const width = SAMPLE_WIDTH;
  const height = Math.round((video.videoHeight / video.videoWidth) * width) || 160;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return { sharpness: 0, change: 0 };
  context.drawImage(video, 0, 0, width, height);
  const { data } = context.getImageData(0, 0, width, height);

  const grey = new Float32Array(width * height);
  for (let i = 0; i < grey.length; i += 1) {
    grey[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  }

  let sum = 0;
  let sumSquares = 0;
  let counted = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const laplacian = 4 * grey[i] - grey[i - 1] - grey[i + 1] - grey[i - width] - grey[i + width];
      sum += laplacian;
      sumSquares += laplacian * laplacian;
      counted += 1;
    }
  }
  const mean = sum / counted;

  // Against the previous frame only when it was the same size: rotating a
  // phone changes the geometry, and comparing across that would read as a
  // scene change when nothing was swapped.
  let change = 0;
  const before = memory.grey;
  if (before && before.length === grey.length) {
    let difference = 0;
    for (let i = 0; i < grey.length; i += 1) difference += Math.abs(grey[i] - before[i]);
    change = difference / grey.length;
  }
  memory.grey = grey;

  return { sharpness: sumSquares / counted - mean * mean, change };
}
